import { formatDisplayNumber, getProjectCurrencySign } from '@stokr/components-library'
import { parseTemplatePath, resolveProjectField, type MoneyDisplay } from 'shared/domain/projectTemplate'
import {
  GOLD_CURRENCY,
  isComputedFieldPath,
  type ComputedFieldPath,
  type ComputedFields,
  type ComputedValue,
} from 'models/productChart'

/**
 * STO Metrics — the hero figures at the top of an STO's public page
 * (`hero-metrics`) and its Key Information panel (`key-information`). Both
 * are authored in back-office (`src/models/metric.ts` there) and share the
 * same value model: a figure is either a value read live off the project
 * record, or a string an author typed. This file ports the read-side pieces
 * of that model byte-faithfully so resolution here matches back-office's own
 * preview exactly — see `resolveMetricValue` below.
 */

export interface MetricValueSource {
  kind: 'field' | 'literal'
  value: string
}

/** One row of a hero metric's expanded panel — "Definition", "As of",
 * "Source", or anything else an author wants. Not a fixed set of fields. */
export interface MetricEntry {
  title?: string
  text?: string
  tooltip?: string
  /** Appends the standard legal disclaimer to `tooltip`, as on the figures. */
  tooltipDisclaimer?: boolean
  /** Appends the past performance disclaimer to `tooltip`, as on the figures. */
  tooltipPastPerformanceDisclaimer?: boolean
  /** Optional link: the text renders as a link to it. A text that is itself a URL links too. */
  url?: string
  order?: string
}

/**
 * A labelled value, e.g. "Type: Structured Note": one Key Information row, one figure of the
 * latest dividend, and the base of a `Metric`.
 */
export interface LabelledFigure {
  key: string
  label: string
  order?: string
  value: MetricValueSource
  /** Shown next to the label, e.g. the ⓘ beside "Total Return (1Y)". */
  tooltip?: string
  /** Appends the standard legal disclaimer to `tooltip` — only meaningful when `tooltip` is set. */
  tooltipDisclaimer?: boolean
  /** Appends the past performance disclaimer to `tooltip` — only meaningful when `tooltip` is set. */
  tooltipPastPerformanceDisclaimer?: boolean
  /** Optional external link — when set, the value renders as a link to this URL. */
  url?: string
}

/** A hero metric or key term: a labelled figure that can expand into a panel of entries. */
export interface Metric extends LabelledFigure {
  entries?: MetricEntry[]
  note?: string
}

/** A Key Information sub-header and the rows under it, e.g. "Asset Contract" →
 * Network, Contract Address, Token supply. */
export interface KeyInformationSection {
  key: string
  label: string
  order?: string
  entries: LabelledFigure[]
}

/** Shown in a computed metric's place while the chart it comes from loads (client-only). */
export const COMPUTED_PENDING = '—'

/**
 * A change as the chart shows it (`+6.72%`); an amount in full (`$15,769,230`, `4,321.50 XAUt`), or to
 * its `decimals` (`$97.72`, `₿0.39437521`). A currency without a sign (a stock in SEK) reads with its code.
 */
const formatComputed = ({ value, currency, decimals = 0 }: ComputedValue): string => {
  if (!currency)
    return `${value > 0 ? '+' : ''}${formatDisplayNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
  if (currency === GOLD_CURRENCY)
    return `${formatDisplayNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
  const number = formatDisplayNumber(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  const sign = getProjectCurrencySign(currency)
  return sign && sign !== currency ? `${sign}${number}` : `${number} ${currency}`
}

const resolveComputedField = (path: string, computed: ComputedFields | undefined): string | undefined => {
  if (computed?.pending) return COMPUTED_PENDING
  const value = computed?.values[path as ComputedFieldPath]
  return value === undefined ? undefined : formatComputed(value)
}

/** Resolves a metric/key-information value against the current project
 * record — a `literal` value is used as-is, a `field` value (`{{path}}`, or a bare
 * path as back-office accepts) is read and formatted by `shared/domain/projectTemplate`;
 * a `{{computed.…}}` one comes from the product's chart (`computed`). */
export const resolveMetricValue = (
  source: MetricValueSource | undefined,
  project: unknown,
  display?: MoneyDisplay,
  computed?: ComputedFields,
): string | undefined => {
  if (!source || !source.value) return undefined
  if (source.kind === 'literal') return source.value
  const path = parseTemplatePath(source.value) ?? source.value.trim()
  if (isComputedFieldPath(path)) return resolveComputedField(path, computed)
  return resolveProjectField(path, project, display)
}

export const isMetricLinkValue = (value: string | undefined): boolean => !!value && /^https?:\/\//i.test(value)

/** Only web and mail links: whatever else back-office holds (`javascript:`, a typo) links nowhere. */
const SAFE_LINK = /^(https?:\/\/|mailto:)/i

/** The value is always what is shown; `url`, or a value that is itself a URL, is where it links. */
export const getMetricHref = (value: string, url?: string): string | undefined => {
  const href = url?.trim() || (isMetricLinkValue(value) ? value : undefined)
  return href && SAFE_LINK.test(href) ? href : undefined
}
